import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [session, setSession] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [profileLoading, setProfileLoading] = useState(false);

    useEffect(() => {
        let mounted = true;
        supabase.auth.getSession().then(({ data }) => {
            if (!mounted) return;
            setSession(data.session || null);
            setLoading(false);
        });
        const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
            setSession(sess || null);
        });
        return () => {
            mounted = false;
            sub.subscription?.unsubscribe?.();
        };
    }, []);

    useEffect(() => {
        async function loadProfile() {
            if (!session?.user) { setProfile(null); return; }
            setProfileLoading(true);
            try {
                const { data, error } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', session.user.id)
                    .maybeSingle();
                if (error) throw error;
                setProfile(data || null);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('profile load error', e);
                setProfile(null);
            } finally {
                setProfileLoading(false);
            }
        }
        loadProfile();
    }, [session?.user?.id]);

    const signOut = async () => {
        await supabase.auth.signOut();
        setProfile(null);
    };

    const refreshProfile = async () => {
        if (!session?.user) return;
        const { data } = await supabase.from('profiles').select('*').eq('id', session.user.id).maybeSingle();
        setProfile(data || null);
    };

    const value = useMemo(
        () => ({ session, user: session?.user || null, profile, loading, profileLoading, signOut, refreshProfile }),
        [session, profile, loading, profileLoading]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
